import React, { ReactElement, memo, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import FavoriteButton from '../../../atoms/buttons/FavoriteButton';
import FavoriteOfferButton from '../../../atoms/buttons/FavoriteOfferButton';
import { renderAvatar, toaster } from '../../../common';
import FavOfferCreate from '../Offers/FavOfferCreate';
import OfferSuccess from '../Offers/OfferSuccess';
import OfferAccept from '../Offers/OfferAccept';
import OfferAcceptUnlock from '../Offers/OfferAcceptUnlock';
import OfferAcceptMessage from '../Offers/OfferAcceptMessage';
import useModal from '../../../hooks/useModal';
import { declineOffer } from '../../../actions/profileTrackAction';
import { useNavigate } from 'react-router-dom';
import { AccountMaker } from '../../../models/Account';
import { useDispatch, useSelector } from 'react-redux';
import { acceptOffer, counterOffer, createNewOffer, ignoreOffer, sendSuggestion } from '../../../actions/offerActions';
import { RootState } from '../../../store';
import { FavoriteActionButtonProps, FavoriteActionWrapProps } from './types/FavoriteActionButton';
import useMediaBreakpoints from '../../../hooks/useMediaBreakpoints';
import { mixpanelTrackEvent } from '../../../utils/mixpanel/common';
import { EVENT_NAMES } from '../../../constants/mixpanel';
import { isDarkMode } from '../../../utils/helpers';
import useMandatoryWall from '../../../hooks/useMandatoryWall';
import { useCommonModal } from 'contexts/ModalContext';
import breakpoints from 'theme/material/breakpoints';

const FavoriteActionWrap = styled('div')<FavoriteActionWrapProps>(
  {
    margin: 0,
    justifyContent: 'middle',
    marginRight: 20,
    alignSelf: 'center',
    [breakpoints.down(639.05)]: {
      marginRight: 0,
    },
  },
  ({ viewType }) =>
    viewType === 'grid' && {
      marginRight: 0,
    }
);

const ViewedMeButtonWrap = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  flexFlow: 'row',
  alignItems: 'center',
});

const FavoriteActionButton = ({
  favKey,
  favorited,
  toggleTracking,
  offer,
  page,
  viewType,
  otherAccount,
  isMatched,
  isSuggestRequest,
  pageTitle,
  enableSlider,
}: FavoriteActionButtonProps): ReactElement => {
  const user = useSelector((state: RootState) => state.profile);
  const dispatch = useDispatch();
  const [showOfferSuccessModal, setShowOfferSuccessModal] = useState({
    isOpen: false,
    amount: '',
    isCounter: false,
  });

  const [showOfferAcceptModal, setShowOfferAcceptModal] = useState(false);
  const [showFavOfferCreateModal, setShowFavOfferCreateModal] = useState(false);
  const [showOfferAcceptUnlockModal, setShowOfferAcceptUnlockModal] = useState({
    isOpen: false,
    amount: '',
    creditCost: null,
    threadUid: '',
  });
  const [showOfferAcceptMessageModal, setShowOfferAcceptMessageModal] = useState({ isOpen: false, amount: '' });
  const { isMobile } = useMediaBreakpoints();
  const hasOffer = offer !== undefined && offer.data !== undefined;
  const offerData = offer && offer.data;
  const offerAccepted = hasOffer && offerData.status === 'accepted';
  const offerIsFromOtherUser =
    hasOffer && offerData.status === 'pending' && offerData.to_account_hash_id === user.hash_id;
  const otherProfile = otherAccount.profile.data;
  const isGenerous = otherProfile.account_type === 'Attractive';
  const canSendOffer = isMatched && !offer;
  const userGender = user && user?.profile?.data?.sex;
  const accountModel = AccountMaker.create(user);
  const { setShowTooManyPendingOfferModal, setModalArgs } = useCommonModal();

  const themeDarkMode = isDarkMode();

  const offerTextGrid = isGenerous ? 'Make Offer' : 'Request Offer';
  const offerTextList = isGenerous ? 'Offer' : 'Request';

  const navigate = useNavigate();

  const { resetModal } = useModal();
  const { showMandatoryWall } = useMandatoryWall();

  const handleGoToConversation = () => {
    navigate(`/mail/${offerData?.thread_uid ?? otherAccount.hash_id}`);
  };

  const handleAttractiveMessage = (amount = '') => {
    showMandatoryWall(
      () => {
        setShowOfferAcceptModal(false);
        setShowOfferAcceptMessageModal({ isOpen: true, amount });
      },
      () => undefined,
      'Accept Offer'
    );
  };

  const handleAcceptWithMessage = (dealMessage: string) => {
    if (accountModel.shouldShowTooManyLockedOffersDeals()) {
      setShowTooManyPendingOfferModal(true);
      return;
    }

    const newPageTitle = !enableSlider ? '2Col Viewed Me' : pageTitle;

    return dispatch(acceptOffer(otherAccount.hash_id, dealMessage, true, favKey, page, false, newPageTitle, navigate));
  };

  const handleIgnoreOffer = async () => {
    if (accountModel.shouldShowTooManyLockedOffersDeals()) {
      setShowTooManyPendingOfferModal(true);
      return;
    }

    await dispatch(ignoreOffer(otherAccount.hash_id, false, true, favKey, page));
    declineOffer();

    mixpanelTrackEvent({
      eventName: EVENT_NAMES.OFFER_DECLINED,
      payload: {
        eventProperties: {
          Amount: offer.data.price_original,
          User: user.hash_id,
          Source: pageTitle,
        },
      },
      otherAccountHashId: offer.data.initiator_hash_id,
    });

    toaster.warn('Offer Declined');

    resetModal();
  };

  const handleOfferSuccess = (toggle: boolean, amount = '', isCounter = false) => {
    setShowOfferAcceptModal(false);
    setShowOfferSuccessModal({
      isOpen: toggle,
      amount: amount,
      isCounter,
    });
  };

  const handleAcceptUnlock = (amount = '', creditCost, threadUid = '') => {
    showMandatoryWall(() => {
      if (accountModel.shouldShowTooManyLockedOffersDeals('accept')) {
        setShowTooManyPendingOfferModal(true);
        return;
      }

      setShowOfferAcceptModal(false);

      setShowOfferAcceptUnlockModal({
        isOpen: true,
        amount,
        creditCost: creditCost,
        threadUid,
      });
    });
  };

  const handleToggleFavorite = () => {
    showMandatoryWall(
      () => {
        toggleTracking();
      },
      () => undefined,
      'Favorite',
      false,
      ['emailWall', 'profileWall']
    );
  };

  const renderFavoriteButton = useMemo(() => {
    return <FavoriteButton favorited={favorited} page={page} onClick={handleToggleFavorite} />;
  }, [favorited, page, themeDarkMode]);

  const renderSuggestButton = () => {
    return (
      <FavoriteOfferButton
        data-test-id="favorite-btn-offer"
        hasOffer={hasOffer}
        offer={offer}
        type="offer"
        contentText={viewType === 'list' && isMobile ? offerTextList : offerTextGrid}
        customLabelStyle={
          !isGenerous && (viewType === 'grid' || (viewType === 'list' && !isMobile))
            ? { maxWidth: isMobile ? 61 : 73, textAlign: 'center' }
            : {}
        }
        customButtonStyle={viewType === 'list' && isMobile ? { margin: '5px 10px' } : {}}
        onClick={() => {
          showMandatoryWall(
            () => {
              if (!user.can_make_offer && isGenerous && accountModel.shouldShowTooManyLockedOffersDeals()) {
                setShowTooManyPendingOfferModal(true);
                return;
              }

              setShowFavOfferCreateModal(true);
            },
            () => undefined,
            'Suggest Offer'
          );
        }}
      />
    );
  };

  const renderAcceptButton = useMemo(() => {
    if (!offerIsFromOtherUser) {
      return '';
    }

    if (isSuggestRequest) {
      return renderSuggestButton();
    }

    return (
      <FavoriteOfferButton
        data-test-id="favorite-btn-accept"
        hasOffer={hasOffer}
        offer={offer}
        page={page}
        viewType={viewType}
        type="accept"
        onClick={() => {
          showMandatoryWall(
            () => {
              setShowOfferAcceptModal(true);
            },
            () => undefined,
            'Accept Offer'
          );
        }}
      />
    );
  }, [offerIsFromOtherUser, isSuggestRequest, hasOffer, offer, viewType, otherProfile, themeDarkMode]);

  const renderSendOfferButton = useMemo(() => {
    if (!canSendOffer || hasOffer || offerAccepted || offerIsFromOtherUser || isSuggestRequest) {
      return '';
    }

    return (
      <FavoriteOfferButton
        data-test-id="favorite-btn-offer"
        hasOffer={hasOffer}
        offer={offer}
        page={page}
        viewType={viewType}
        type="offer"
        contentText={viewType === 'list' && isMobile ? offerTextList : offerTextGrid}
        customLabelStyle={
          !isGenerous && (viewType === 'grid' || (viewType === 'list' && !isMobile))
            ? { maxWidth: isMobile ? 61 : 73, textAlign: 'center' }
            : {}
        }
        customButtonStyle={viewType === 'list' && isMobile ? { margin: '5px 10px' } : {}}
        onClick={() => {
          showMandatoryWall(
            () => {
              if (!user.can_make_offer && isGenerous) {
                if (accountModel.shouldShowTooManyLockedOffersDeals()) {
                  setShowTooManyPendingOfferModal(true);
                  return;
                } else if (accountModel.shouldShowTooManySentOffers()) {
                  setShowTooManyPendingOfferModal(true);
                  setModalArgs({ type: 'sent' });
                  return;
                }
              }

              setShowFavOfferCreateModal(true);
            },
            () => undefined,
            'Make Offer'
          );
        }}
      />
    );
  }, [
    canSendOffer,
    hasOffer,
    offerAccepted,
    offerIsFromOtherUser,
    isSuggestRequest,
    otherProfile,
    user,
    viewType,
    themeDarkMode,
  ]);

  const handleCreateNewOffer = formData => {
    if (accountModel.shouldShowTooManyLockedOffersDeals()) {
      setShowTooManyPendingOfferModal(true);
      return;
    }

    const newPageTitle = !enableSlider ? '2Col Viewed Me' : pageTitle;

    return dispatch(createNewOffer({ ...formData, source: newPageTitle }, true, favKey, page));
  };

  const handleCounterOffer = formData => {
    if (accountModel.shouldShowTooManyLockedOffersDeals()) {
      setShowTooManyPendingOfferModal(true);
      return;
    }

    return dispatch(counterOffer(formData, true, navigate, favKey, page));
  };

  const handleSuggestion = profileId => {
    if (accountModel.shouldShowTooManyLockedOffersDeals()) {
      setShowTooManyPendingOfferModal(true);
      return;
    }

    return dispatch(sendSuggestion(profileId, true, favKey, page));
  };

  const handleCloseOfferAcceptUnlock = () => {
    setShowOfferAcceptUnlockModal({
      isOpen: false,
      amount: '',
      creditCost: null,
      threadUid: '',
    });
  };

  return (
    <FavoriteActionWrap viewType={viewType}>
      <ViewedMeButtonWrap
        style={
          !isGenerous && (viewType === 'grid' || (viewType === 'list' && !isMobile)) ? { alignItems: 'flex-start' } : {}
        }
      >
        {renderFavoriteButton}
        {renderSendOfferButton}
        {renderAcceptButton}
        {hasOffer && offerAccepted && (
          <FavoriteOfferButton
            data-test-id="favorite-btn-conversation"
            hasOffer={hasOffer}
            offer={offer}
            viewType={viewType}
            page={page}
            onClick={handleGoToConversation}
            type="conversation"
            style={{ margin: '0px 0px 0px 0px' }}
          />
        )}
      </ViewedMeButtonWrap>

      <OfferSuccess
        isModalOpen={showOfferSuccessModal.isOpen}
        onClose={() => {
          setShowOfferSuccessModal({
            isOpen: false,
            amount: '',
            isCounter: false,
          });
        }}
        offerAmount={showOfferSuccessModal.amount}
        avatarSrc={renderAvatar(otherProfile.avatar.data.urls, true)}
        username={otherAccount.username}
        buttonID="interestedOffers"
        isCounter={showOfferSuccessModal.isCounter}
      />

      {otherProfile && offer && (
        <OfferAccept
          isModalOpen={showOfferAcceptModal}
          acceptUnlock={handleAcceptUnlock}
          avatarSrc={renderAvatar(otherProfile.avatar.data.urls, true)}
          username={otherAccount.username}
          hashId={otherAccount.hash_id}
          onHandleSubmit={() => {
            if (accountModel.shouldShowTooManyLockedOffersDeals('accept')) {
              setShowTooManyPendingOfferModal(true);
              return;
            }

            const newPageTitle = !enableSlider ? '2Col Viewed Me' : pageTitle;

            return dispatch(acceptOffer(otherAccount.hash_id, '', true, favKey, page, false, newPageTitle, navigate));
          }}
          onHandleAttractiveSubmit={handleAttractiveMessage}
          successOpen={handleOfferSuccess}
          counterOffer={handleCounterOffer}
          closeModal={() => setShowOfferAcceptModal(false)}
          currentUserIsGenerous={isGenerous}
          // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
          sendSuggestion={() => handleSuggestion(otherAccount)}
          cannot_make_offer_reasons={user.cannot_make_offer_reasons}
          ignoreStatus={() => null}
          justSuggested={false}
          buttonID="acceptOffer"
          offer={offer}
          ignoreOffer={handleIgnoreOffer}
          pageTitle={pageTitle}
        />
      )}

      {otherProfile && (
        <>
          <OfferAcceptMessage
            isModalOpen={showOfferAcceptMessageModal.isOpen}
            offerAmount={showOfferAcceptMessageModal.amount}
            avatarSrc={renderAvatar(otherProfile.avatar.data.urls, true)}
            username={otherAccount.username}
            buttonID="interestedOffers"
            closeModal={() => {
              setShowOfferAcceptMessageModal({ isOpen: false, amount: '' });
            }}
            onSubmit={handleAcceptWithMessage}
          />
          <OfferAcceptUnlock
            isModalOpen={showOfferAcceptUnlockModal.isOpen}
            offerAmount={showOfferAcceptUnlockModal.amount}
            avatarSrc={renderAvatar(otherProfile.avatar.data.urls, true)}
            username={otherAccount.username}
            successOpen={handleCloseOfferAcceptUnlock}
            buttonID="interestedOffers"
            currentUserIsGenerous={isGenerous}
            creditCost={showOfferAcceptUnlockModal?.creditCost}
            currentCredit={user.credits}
            hashId={otherAccount.hash_id}
            closeModal={handleCloseOfferAcceptUnlock}
          />
          <FavOfferCreate
            isModalOpen={showFavOfferCreateModal}
            avatarSrc={renderAvatar(otherProfile.avatar.data.urls, true)}
            username={otherAccount.username}
            hashId={otherAccount.hash_id}
            createNewOffer={handleCreateNewOffer}
            successOpen={handleOfferSuccess}
            counterOffer={handleCounterOffer}
            isCounter={false}
            currencyInitial={otherAccount.currency.data.initial}
            currencyLabel={otherAccount.currency.data.label_unicode}
            currentUserIsGenerous={isGenerous}
            sex={userGender === 'Male' ? 'Female' : 'Male'}
            userGender={userGender}
            sendSuggestion={handleSuggestion}
            cannot_make_offer_reasons={user.cannot_make_offer_reasons}
            buttonID="interestedOffers"
            isSuggestRequest={isSuggestRequest}
            pageTitle={pageTitle}
            otherProfile={otherProfile}
            title={isGenerous ? 'Make an Offer!' : 'Request A Date!'}
            closeModal={() => {
              setShowFavOfferCreateModal(false);
            }}
          />
        </>
      )}
    </FavoriteActionWrap>
  );
};

export default memo(FavoriteActionButton);
